/**
 * Centralized Event Manager for Salesforce Chatbot Integration
 *
 * Provides a simple, non-blocking event system for external integrations (like RDM)
 * to listen to chatbot events without coupling to the internal React architecture.
 *
 * Pattern: Observer/EventEmitter with singleton-like module pattern
 *
 * Usage:
 *   import { addEventListener, removeEventListener, EVENT_TYPES } from './eventManager';
 *
 *   // Listen to events
 *   const handler = (eventData) => console.log('New message:', eventData);
 *   addEventListener(EVENT_TYPES.ON_NEW_MESSAGE, handler);
 *
 *   // Clean up
 *   removeEventListener(EVENT_TYPES.ON_NEW_MESSAGE, handler);
 */

/**
 * Event type constants for external consumption
 * These are the public API for integrations like RDM
 */
export const EVENT_TYPES = {
    ON_NEW_MESSAGE: 'onNewMessage',
    ON_AGENT_LEFT: 'onAgentLeft',
    ON_CLOSE: 'onClose'
};

/**
 * Internal registry of event listeners
 * Structure: { eventType: Set<Function> }
 * Using Set to avoid duplicate listeners and provide O(1) deletion
 */
const eventListeners = {
    [EVENT_TYPES.ON_NEW_MESSAGE]: new Set(),
    [EVENT_TYPES.ON_AGENT_LEFT]: new Set(),
    [EVENT_TYPES.ON_CLOSE]: new Set()
};

/**
 * Flag to enable/disable debug logging
 * Set to false in production to avoid console spam
 */
let debugMode = process.env.NODE_ENV === 'development';

/**
 * Internal logger for debugging events
 * @param {string} message - Log message
 * @param {*} data - Optional data to log
 */
function logDebug(message, data = null) {
    if (debugMode) {
        if (data) {
            console.log(`[EventManager] ${message}`, data);
        } else {
            console.log(`[EventManager] ${message}`);
        }
    }
}

/**
 * Validates that an event type is supported
 * @param {string} eventType - Event type to validate
 * @throws {Error} if event type is not supported
 */
function validateEventType(eventType) {
    if (!Object.values(EVENT_TYPES).includes(eventType)) {
        throw new Error(`Unsupported event type: ${eventType}. Supported types: ${Object.values(EVENT_TYPES).join(', ')}`);
    }
}

/**
 * Validates that a handler is a function
 * @param {*} handler - Handler to validate
 * @throws {Error} if handler is not a function
 */
function validateHandler(handler) {
    if (typeof handler !== 'function') {
        throw new Error(`Event handler must be a function, received: ${typeof handler}`);
    }
}

/**
 * Register an event listener for a specific event type
 *
 * @param {string} eventType - One of EVENT_TYPES constants
 * @param {Function} handler - Callback function to invoke when event is dispatched
 * @throws {Error} if eventType is invalid or handler is not a function
 *
 * @example
 * addEventListener(EVENT_TYPES.ON_NEW_MESSAGE, (data) => {
 *   console.log('Message:', data.message);
 * });
 */
export function addEventListener(eventType, handler) {
    try {
        validateEventType(eventType);
        validateHandler(handler);

        eventListeners[eventType].add(handler);
        logDebug(`Listener registered for ${eventType}. Total listeners: ${eventListeners[eventType].size}`);
    } catch (error) {
        console.error(`[EventManager] Failed to add event listener:`, error);
        throw error;
    }
}

/**
 * Unregister an event listener for a specific event type
 *
 * @param {string} eventType - One of EVENT_TYPES constants
 * @param {Function} handler - The same handler function that was registered
 * @returns {boolean} - True if handler was found and removed, false otherwise
 *
 * @example
 * const handler = (data) => console.log(data);
 * addEventListener(EVENT_TYPES.ON_NEW_MESSAGE, handler);
 * // Later...
 * removeEventListener(EVENT_TYPES.ON_NEW_MESSAGE, handler);
 */
export function removeEventListener(eventType, handler) {
    try {
        validateEventType(eventType);
        validateHandler(handler);

        const wasRemoved = eventListeners[eventType].delete(handler);
        if (wasRemoved) {
            logDebug(`Listener removed for ${eventType}. Remaining listeners: ${eventListeners[eventType].size}`);
        } else {
            logDebug(`Listener not found for ${eventType} (already removed?)`);
        }
        return wasRemoved;
    } catch (error) {
        console.error(`[EventManager] Failed to remove event listener:`, error);
        return false;
    }
}

/**
 * Remove all listeners for a specific event type
 * Useful for cleanup or testing
 *
 * @param {string} eventType - One of EVENT_TYPES constants
 * @returns {number} - Number of listeners that were removed
 */
export function removeAllListeners(eventType) {
    try {
        validateEventType(eventType);
        const count = eventListeners[eventType].size;
        eventListeners[eventType].clear();
        logDebug(`All listeners cleared for ${eventType}. Removed: ${count}`);
        return count;
    } catch (error) {
        console.error(`[EventManager] Failed to clear listeners:`, error);
        return 0;
    }
}

/**
 * Dispatch an event to all registered listeners
 * This is a non-blocking operation that catches errors in individual handlers
 * to prevent one failing handler from breaking others.
 *
 * @param {string} eventType - One of EVENT_TYPES constants
 * @param {object} eventData - Event payload to send to handlers
 * @returns {object} - Statistics about the dispatch (for debugging)
 *
 * @example
 * dispatchEvent(EVENT_TYPES.ON_NEW_MESSAGE, {
 *   messageType: 'agent',
 *   content: 'Hello!',
 *   timestamp: Date.now()
 * });
 */
export function dispatchEvent(eventType, eventData) {
    try {
        validateEventType(eventType);

        const listeners = eventListeners[eventType];
        const listenerCount = listeners.size;

        if (listenerCount === 0) {
            logDebug(`No listeners for ${eventType}. Event data:`, eventData);
            return { dispatched: 0, failed: 0 };
        }

        logDebug(`Dispatching ${eventType} to ${listenerCount} listener(s)`, eventData);

        let successCount = 0;
        let failureCount = 0;

        // Iterate over listeners and invoke each one
        // Errors in individual handlers are caught to prevent cascading failures
        listeners.forEach((handler) => {
            try {
                handler(eventData);
                successCount++;
            } catch (error) {
                failureCount++;
                console.error(
                    `[EventManager] Error in event handler for ${eventType}:`,
                    error,
                    '\nEvent data:',
                    eventData
                );
            }
        });

        logDebug(`Dispatch complete for ${eventType}. Success: ${successCount}, Failed: ${failureCount}`);

        return { dispatched: successCount, failed: failureCount };
    } catch (error) {
        console.error(`[EventManager] Failed to dispatch event:`, error);
        return { dispatched: 0, failed: 0 };
    }
}

/**
 * Enable or disable debug logging
 * @param {boolean} enabled - True to enable, false to disable
 */
export function setDebugMode(enabled) {
    debugMode = !!enabled;
    logDebug(`Debug mode ${enabled ? 'enabled' : 'disabled'}`);
}

/**
 * Get current listener count for an event type (for debugging/testing)
 * @param {string} eventType - One of EVENT_TYPES constants
 * @returns {number} - Number of registered listeners
 */
export function getListenerCount(eventType) {
    try {
        validateEventType(eventType);
        return eventListeners[eventType].size;
    } catch (error) {
        return 0;
    }
}

/**
 * Get statistics about all event listeners (for debugging/monitoring)
 * @returns {object} - Object with listener counts per event type
 */
export function getEventStats() {
    return {
        [EVENT_TYPES.ON_NEW_MESSAGE]: eventListeners[EVENT_TYPES.ON_NEW_MESSAGE].size,
        [EVENT_TYPES.ON_AGENT_LEFT]: eventListeners[EVENT_TYPES.ON_AGENT_LEFT].size,
        [EVENT_TYPES.ON_CLOSE]: eventListeners[EVENT_TYPES.ON_CLOSE].size,
        total: Object.values(eventListeners).reduce((sum, set) => sum + set.size, 0)
    };
}

/**
 * Clear all listeners for all event types
 * Use with caution - mainly for cleanup or testing
 */
export function clearAllListeners() {
    Object.keys(eventListeners).forEach(eventType => {
        eventListeners[eventType].clear();
    });
    logDebug('All listeners cleared for all event types');
}

// Export for testing or advanced use cases
export { eventListeners as __eventListeners__ };
